import { useMutation } from '@apollo/client'

import { MUTATION_CREATE_FILE_DRAFT, MUTATION_VALIDATE_FILE } from '@libs/apollo/mutations'

import { FileDraft, MutationCreateFileDraftArgs, MutationValidateFileArgs, LocalFile } from '@types'
import { UploadOptions, useUploadFile } from './upload-file.hook'

interface DataCreateFileDraft {
  createFileDraft: FileDraft
}

interface DataValidateFile {
  validateFile: boolean
}

interface Data {
  id: string
}

export const useCreateFile = (): {
  createFile: (file: LocalFile, options?: UploadOptions) => Promise<Data | undefined>
  loading: boolean
} => {
  const { uploadFile } = useUploadFile()
  const [createFileDraft, { loading: isLoadingCreate }] = useMutation<DataCreateFileDraft, MutationCreateFileDraftArgs>(
    MUTATION_CREATE_FILE_DRAFT,
    {
      notifyOnNetworkStatusChange: true,
    },
  )

  const [validateFile, { loading: isLoadingValidate }] = useMutation<DataValidateFile, MutationValidateFileArgs>(
    MUTATION_VALIDATE_FILE,
    {
      notifyOnNetworkStatusChange: true,
    },
  )

  const _createFile = async (file: LocalFile, options?: UploadOptions): Promise<Data | undefined> => {
    try {
      const fileDraft = await createFileDraft({
        variables: {
          input: {
            name: file.name,
            extension: file.name.split('.').pop() || '',
            type: file.type,
          },
        },
      })

      if (fileDraft.data?.createFileDraft) {
        const { id: fileId, uploadLink } = fileDraft.data?.createFileDraft

        await uploadFile(uploadLink, file, options)
        await validateFile({ variables: { input: { fileId } } })

        return { id: fileId }
      }

      throw new Error('File ')
    } catch (error) {
      console.error(error)
    }
  }

  return { createFile: _createFile, loading: isLoadingValidate || isLoadingCreate }
}
